import Pako from "pako";
import useUsers from "../hooks/useUsers";
import _, { isNaN } from "lodash";

/**
 * Compress string using gzip
 * @param {string} input - string to be compressed
 * @returns {string} - gzip compressed string
 */
export const compress = (input) => {
  try {
    return window.btoa(
      Array.from(Pako.gzip(input)).reduce(function (data, byte) {
        return data + String.fromCharCode(byte);
      }, "")
    );

    // return window.btoa(
    //   String.fromCharCode.apply(null, Array.from(Pako.gzip(input)))
    // );
  } catch (error) {
    console.error(error);
    return null;
  }
};

/**
 * Decompress gzip compressed string
 * @param {string} data
 * @returns {string} - decompressed string
 */
export function decompress(data) {
  const binaryString = window.atob(data);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return Pako.ungzip(bytes.buffer, { to: "string" });
}

/**
 * Human readable elapsed or remaining time (example: 3 minutes ago)
 * @param  {Date|Number|String} date A Date object, timestamp or string parsable with Date.parse()
 * @param  {Date|Number|String} [nowDate] A Date object, timestamp or string parsable with Date.parse()
 * @param  {Intl.RelativeTimeFormat} [trf] A Intl formater
 * @return {string} Human readable elapsed or remaining time
 * @author github.com/victornpb
 * @see https://stackoverflow.com/a/67338038/938822
 */
export function fromNow(
  date,
  nowDate = Date.now(),
  rft = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" })
) {
  const SECOND = 1000;
  const MINUTE = 60 * SECOND;
  const HOUR = 60 * MINUTE;
  const DAY = 24 * HOUR;
  const WEEK = 7 * DAY;
  const YEAR = 365 * DAY;
  const MONTH = YEAR / 12;
  const intervals = [
    { ge: YEAR, divisor: YEAR, unit: "year" },
    { ge: MONTH, divisor: MONTH, unit: "month" },
    { ge: WEEK, divisor: WEEK, unit: "week" },
    { ge: DAY, divisor: DAY, unit: "day" },
    { ge: HOUR, divisor: HOUR, unit: "hour" },
    { ge: MINUTE, divisor: MINUTE, unit: "minute" },
    { ge: 30 * SECOND, divisor: SECOND, unit: "seconds" },
    { ge: 0, divisor: 1, text: "just now" },
  ];
  const now =
    typeof nowDate === "object"
      ? nowDate.getTime()
      : new Date(nowDate).getTime();
  const diff =
    now - (typeof date === "object" ? date : new Date(date)).getTime();
  const diffAbs = Math.abs(diff);
  for (const interval of intervals) {
    if (diffAbs >= interval.ge) {
      const x = Math.round(Math.abs(diff) / interval.divisor);
      const isFuture = diff < 0;
      return interval.unit
        ? rft.format(isFuture ? x : -x, interval.unit)
        : interval.text;
    }
  }
}

/**
 * Get User Profile paramater
 * @param {*} userId User Id
 * @param {*} name `{ firstname, surname }`
 * @returns
 */
export async function getUserLink(userId, name) {
  const { queryUsers } = useUsers();

  let _firstname = _.cloneDeep(name?.firstname);
  let _surname = _.cloneDeep(name?.surname);

  if (userId) {
    const { data: usersFound } = await queryUsers({ _id: userId }, 1, 1, {
      firstname: 1,
      surname: 1,
    });

    if (!usersFound || usersFound.length === 0) {
      return undefined;
    }

    _firstname = usersFound[0].firstname;
    _surname = usersFound[0].surname;
  }

  // * Check user's name has a duplicate
  const { data: duplicateUsers } = await queryUsers(
    { firstname: { $regex: _firstname } },
    1,
    1,
    {
      _id: 1,
    }
  );

  if (duplicateUsers.length === 1) {
    return _firstname.toLowerCase();
  }

  return `${_firstname.toLowerCase()}-${_surname.toLowerCase()}`;
}

export function isValidNumber(value) {
  const _value = value.toString();

  if (!_value) return false;

  if (_value === "-" || _value === "--") {
    return false;
  }

  if (_.isNaN(_value)) return false;

  return +_value >= 0;
}

export function uuidv4() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c == "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function formatNumber(value, currency) {
  return `${currency ?? ""}${
    !value
      ? 0
      : value.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
  }`;
}

export function formatFileSize(size) {
  if (size > 1024 * 1024 * 1024) {
    return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }

  if (size > 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  }

  return `${(size / 1024).toFixed(2)} KB`;
}

export const provinces = [
  { name: "Amnat Charoen", value: "Amnat Charoen" },
  { name: "Ang Thong", value: "Ang Thong" },
  { name: "Bangkok", value: "Bangkok" },
  { name: "Buri Ram", value: "Buri Ram" },
  { name: "Chachoengsao", value: "Chachoengsao" },
  { name: "Chai Nat", value: "Chai Nat" },
  { name: "Chaiyaphum", value: "Chaiyaphum" },
  { name: "Chanthaburi", value: "Chanthaburi" },
  { name: "Chiang Mai", value: "Chiang Mai" },
  { name: "Chiang Rai", value: "Chiang Rai" },
  { name: "Chon Buri", value: "Chon Buri" },
  { name: "Chumphon", value: "Chumphon" },
  { name: "Kalasin", value: "Kalasin" },
  { name: "Kamphaeng Phet", value: "Kamphaeng Phet" },
  { name: "Kanchanaburi", value: "Kanchanaburi" },
  { name: "Khon Kaen", value: "Khon Kaen" },
  { name: "Krabi", value: "Krabi" },
  { name: "Lampang", value: "Lampang" },
  { name: "Lamphun", value: "Lamphun" },
  { name: "Loei", value: "Loei" },
  { name: "Lop Buri", value: "Lop Buri" },
  { name: "Mae Hong Son", value: "Mae Hong Son" },
  { name: "Maha Sarakham", value: "Maha Sarakham" },
  { name: "Mukdahan", value: "Mukdahan" },
  { name: "Nakhon Nayok", value: "Nakhon Nayok" },
  { name: "Nakhon Pathom", value: "Nakhon Pathom" },
  { name: "Nakhon Phanom", value: "Nakhon Phanom" },
  { name: "Nakhon Ratchasima", value: "Nakhon Ratchasima" },
  { name: "Nakhon Sawan", value: "Nakhon Sawan" },
  { name: "Nakhon Si Thammarat", value: "Nakhon Si Thammarat" },
  { name: "Nan", value: "Nan" },
  { name: "Narathiwat", value: "Narathiwat" },
  { name: "Nong Bua Lam Phu", value: "Nong Bua Lam Phu" },
  { name: "Nong Khai", value: "Nong Khai" },
  { name: "Nonthaburi", value: "Nonthaburi" },
  { name: "Pathum Thani", value: "Pathum Thani" },
  { name: "Pattani", value: "Pattani" },
  { name: "Phangnga", value: "Phangnga" },
  { name: "Phatthalung", value: "Phatthalung" },
  { name: "Phayao", value: "Phayao" },
  { name: "Phetchabun", value: "Phetchabun" },
  { name: "Phetchaburi", value: "Phetchaburi" },
  { name: "Phichit", value: "Phichit" },
  { name: "Phitsanulok", value: "Phitsanulok" },
  { name: "Phra Nakhon Si Ayutthaya", value: "Phra Nakhon Si Ayutthaya" },
  { name: "Phrae", value: "Phrae" },
  { name: "Phuket", value: "Phuket" },
  { name: "Prachin Buri", value: "Prachin Buri" },
  { name: "Prachuap Khiri Khan", value: "Prachuap Khiri Khan" },
  { name: "Ranong", value: "Ranong" },
  { name: "Ratchaburi", value: "Ratchaburi" },
  { name: "Rayong", value: "Rayong" },
  { name: "Roi Et", value: "Roi Et" },
  { name: "Sa Kaeo", value: "Sa Kaeo" },
  { name: "Sakon Nakhon", value: "Sakon Nakhon" },
  { name: "Samut Prakan", value: "Samut Prakan" },
  { name: "Samut Sakhon", value: "Samut Sakhon" },
  { name: "Samut Songkhram", value: "Samut Songkhram" },
  { name: "Saraburi", value: "Saraburi" },
  { name: "Satun", value: "Satun" },
  { name: "Si Sa Ket", value: "Si Sa Ket" },
  { name: "Sing Buri", value: "Sing Buri" },
  { name: "Songkhla", value: "Songkhla" },
  { name: "Sukhothai", value: "Sukhothai" },
  { name: "Suphan Buri", value: "Suphan Buri" },
  { name: "Surat Thani", value: "Surat Thani" },
  { name: "Surin", value: "Surin" },
  { name: "Tak", value: "Tak" },
  { name: "Trang", value: "Trang" },
  { name: "Trat", value: "Trat" },
  { name: "Ubon Ratchathani", value: "Ubon Ratchathani" },
  { name: "Udon Thani", value: "Udon Thani" },
  { name: "Uthai Thani", value: "Uthai Thani" },
  { name: "Uttaradit", value: "Uttaradit" },
  { name: "Yala", value: "Yala" },
  { name: "Yasothon", value: "Yasothon" },
];

export const numberToOrdinal = (n) => {
  if (typeof n !== "number") return n;

  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;

  return (n + (s[(v - 20) % 10] || s[v] || s[0])).toString();
};

export const dataURLtoFile = (dataurl, filename) => {
  var arr = dataurl.split(","),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[arr.length - 1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

export const nationalIdValid = (nationalIdNumber) => {
  if (!Number(nationalIdNumber)) return false;
  if (nationalIdNumber.substring(0, 1) === 0) return false;
  if (nationalIdNumber.length !== 13) return false;

  let sum = 0;
  for (let i = 0; i < 12; i++)
    sum += parseFloat(nationalIdNumber.charAt(i)) * (13 - i);

  return (11 - (sum % 11)) % 10 === parseFloat(nationalIdNumber.charAt(12));
};
